import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import submit from "submit-form";
import Modal from "../../../../parts/modal-new";
import {
  getCommunityConversation,
  getGuestsUsersInfo,
  modifyConversation,
} from "../../../../utils/chat-util";
import { ParticipantCard } from "../components/ParticipantCard";
import { SelectCommunityChatType } from "../components/SelectCommunityChatType";

export const EditChannelModal = ({ locals, api }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    flash,
    activeCommunityChatClientsList = [],
    activeUser = {},
    activeUserToRequest = {},
    query,
    usersOrganization = {
      organizationActiveUsers: [],
    },
    allCommunityConversations = {
      readChannels: [],
      readDirects: [],
      unreadChats: [],
    },
  } = locals;
  const { organizationActiveUsers = [] } = usersOrganization || {};

  const { webAppTheme = "dark" } = activeUser;
  const {
    _id: activeUserId,
    avatar: activeUserAvatar,
    name: activeUserName,
  } = activeUserToRequest;
  const { conversationId } = query;
  //we are finding the conversation selected in the conversations array
  const conversationsArray = [
    ...allCommunityConversations.readChannels,
    ...allCommunityConversations.readDirects,
    ...allCommunityConversations.unreadChats,
  ];
  const conversation = findConversationById(conversationsArray, conversationId);
  const [conversationState, setConversationState] = useState(conversation);
  const [isLoading, setIsLoading] = useState(false);
  //we are extracting the properties of the conversation
  const {
    subject,
    custom = {
      blocked: "[]",
      guests: "[]",
    },
    participants = {},
  } = conversationState;
  const [subjectState, setSubjectState] = useState(subject);
  const {
    blocked = "[]",
    guests = "[]",
    isPush = "false",
    isPress = "false",
    isPerform,
    isPull,
  } = custom;
  const initialChatTypes = [];
  if (isPush === "true") initialChatTypes.push("push");
  if (isPress === "true") initialChatTypes.push("press");
  if (isPerform === "true") initialChatTypes.push("perform");
  if (isPull === "true") initialChatTypes.push("pull");
  const [selectedChatType, setSelectedChatType] = useState(initialChatTypes);

  //-------------Init of building the participants array------------------
  const blockedList = useMemo(() => new Set(JSON.parse(blocked)), [blocked]);
  const guestsList = useMemo(() => new Set(JSON.parse(guests)), [guests]);
  const participantIds = useMemo(
    () => new Set(Object.keys(participants)),
    [participants]
  );
  //We are filtering the members of the organization
  const subscriberIdsSet = useMemo(
    () => new Set(activeCommunityChatClientsList.map(({ _id }) => _id)),
    [activeCommunityChatClientsList]
  );

  //Participants that aren't the coach's own subscribers/athletes nor org staff -
  //e.g. athletes pulled in from outside the current coach's scope. We need to
  //fetch their identity separately instead of defaulting them to the viewer.
  const unresolvedParticipantIds = useMemo(() => {
    return Array.from(participantIds).filter((participantId) => {
      if (subscriberIdsSet.has(participantId)) return false;
      if (participantId === activeUserId) return false;
      return !organizationActiveUsers.some(({ _id }) => _id === participantId);
    });
  }, [participantIds, subscriberIdsSet, organizationActiveUsers, activeUserId]);

  //Guests (custom.guests, invited/pulled but not yet an actual conversation
  //participant) that aren't in the coach's own scoped list, aren't already an
  //actual participant (handled above), and aren't org staff - e.g. a sub-coach's
  //athlete pulled into this channel. Same identity-resolution problem as above.
  const unresolvedGuestIds = useMemo(() => {
    return Array.from(guestsList).filter((guestId) => {
      if (subscriberIdsSet.has(guestId)) return false;
      if (participantIds.has(guestId)) return false;
      if (guestId === activeUserId) return false;
      return !organizationActiveUsers.some(({ _id }) => _id === guestId);
    });
  }, [guestsList, subscriberIdsSet, participantIds, organizationActiveUsers, activeUserId]);

  const idsToResolve = useMemo(
    () => Array.from(new Set([...unresolvedParticipantIds, ...unresolvedGuestIds])),
    [unresolvedParticipantIds, unresolvedGuestIds]
  );

  const [unresolvedUsersInfo, setUnresolvedUsersInfo] = useState([]);

  useEffect(() => {
    let isCancelled = false;
    if (!idsToResolve.length) {
      setUnresolvedUsersInfo([]);
      return undefined;
    }
    (async () => {
      const guestsInfo = await getGuestsUsersInfo(api, idsToResolve);
      if (!isCancelled) setUnresolvedUsersInfo(guestsInfo);
    })();
    return () => {
      isCancelled = true;
    };
  }, [api, idsToResolve]);

  const updatedUnresolvedGuestsList = useMemo(() => {
    return unresolvedGuestIds.map((guestId) => {
      const unresolvedUser = unresolvedUsersInfo.find(
        ({ _id }) => _id === guestId
      );
      return {
        _id: guestId,
        name: unresolvedUser?.name || "",
        avatar: unresolvedUser?.avatar || {},
        isBlocked: blockedList.has(guestId),
        isGuest: true,
        isRegistered: true,
        role: "Client",
      };
    });
  }, [unresolvedGuestIds, unresolvedUsersInfo, blockedList]);

  const updatedParticipantsList = useMemo(() => {
    return Array.from(participantIds).reduce((acc, participantId) => {
      if (subscriberIdsSet.has(participantId)) {
        return acc; // We are ignoring if the participant is a subscriber, we only want coaches and org members
      }
      //in case the user isn't an organization organizationUser will be undefined, so the coach will be the owner
      const organizationUser = organizationActiveUsers.find(
        ({ _id }) => _id === participantId
      );

      if (!organizationUser && participantId !== activeUserId) {
        //Participant belongs to neither the current coach's scope nor the org staff list -
        //resolve their identity from the fetched guests info instead of defaulting to the viewer.
        const unresolvedUser = unresolvedUsersInfo.find(
          ({ _id }) => _id === participantId
        );

        acc.push({
          name: unresolvedUser?.name || "",
          _id: participantId,
          isBlocked: blockedList.has(participantId),
          isGuest: false,
          isRegistered: true,
          role: "Client",
          avatar: unresolvedUser?.avatar || {},
        });

        return acc;
      }

      acc.push({
        name: organizationUser ? organizationUser.name : activeUserName,
        _id: organizationUser ? organizationUser._id : activeUserId,
        isBlocked: false,
        isGuest: false,
        isRegistered: true,
        role: organizationUser
          ? organizationUser._id === activeUserId
            ? "Owner"
            : "Organization Member"
          : "Owner",
        avatar: organizationUser ? organizationUser.avatar : activeUserAvatar,
      });

      return acc;
    }, []);
  }, [
    participantIds,
    subscriberIdsSet,
    organizationActiveUsers,
    activeUserId,
    activeUserName,
    activeUserAvatar,
    unresolvedUsersInfo,
    blockedList,
  ]);

  //We are filtering the subscribers
  const updatedSubscribersList = activeCommunityChatClientsList.reduce(
    (acc, subscriber) => {
      const { _id: subscriberId, setup } = subscriber;
      const isGuest =
        guestsList.has(subscriberId) && !participantIds.has(subscriberId);
      const isParticipant = participantIds.has(subscriberId);
      // We are excluding the subscribers if they aren't in the guestsList or participantIds
      if (isGuest || isParticipant) {
        const isBlocked = blockedList.has(subscriberId);
        acc.push({
          ...subscriber,
          isBlocked,
          isGuest,
          isRegistered: setup === "finished",
          role: "Client",
        });
      }

      return acc;
    },
    []
  );

  //We are combining the two arrays
  const combinedList = useMemo(() => {
    const combinedParticipantsMap = new Map();
    updatedParticipantsList.forEach((participant) => {
      combinedParticipantsMap.set(participant._id, participant);
    });
    updatedSubscribersList.forEach((subscriber) => {
      if (!combinedParticipantsMap.has(subscriber._id)) {
        combinedParticipantsMap.set(subscriber._id, subscriber);
      }
    });
    updatedUnresolvedGuestsList.forEach((guest) => {
      if (!combinedParticipantsMap.has(guest._id)) {
        combinedParticipantsMap.set(guest._id, guest);
      }
    });
    return Array.from(combinedParticipantsMap.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
  }, [updatedParticipantsList, updatedSubscribersList, updatedUnresolvedGuestsList]);

  //-------------End of building the participants array------------------

  /**
   * To know if the button is enable or disable
   * @returns
   */

  const isButtonDisabled = () => {
    const noSubjectChange = subjectState === subject;

    const currentTypes = [];
    if (conversationState.custom?.isPush === "true") currentTypes.push("push");
    if (conversationState.custom?.isPress === "true")
      currentTypes.push("press");
    if (conversationState.custom?.isPerform === "true")
      currentTypes.push("perform");
    if (conversationState.custom?.isPull === "true") currentTypes.push("pull");

    const noTypeChange =
      selectedChatType.length === currentTypes.length &&
      selectedChatType.every((t) => currentTypes.includes(t));

    if (noSubjectChange && noTypeChange) return true;
    if (subjectState.trim().length === 0) return true;
    if (selectedChatType.length === 0) return true;

    return false;
  };

  /**
   * Handle the block/unblock make/revoke admin
   * @param {*} option
   * @param {*} participantId
   */
  const handleParticipantAction = useCallback(
    (option, participantId) => {
      const body = {
        option,
        conversationId,
        custom,
        participantId,
      };
      handleModifyConversation(body);
    },
    [conversationId, custom]
  );

  /**
   * Change the name and the type of the channel
   */
  const handleEditChannel = () => {
    const subjectChanged = subjectState !== subject;

    if (subjectChanged) {
      const filteredSubjects = conversationsArray
        .filter((item) => item.subject)
        .map((item) => item.subject);

      if (filteredSubjects.includes(subjectState)) {
        flash("error", t("communityChat.editChannelModal.channelNameExists"));
        return submit(`/actions/community-chat/refresh`, {
          method: "POST",
        });
      }
    }

    const typesChanged =
      initialChatTypes.length !== selectedChatType.length ||
      initialChatTypes.some((t) => !selectedChatType.includes(t));

    const currentParticipants = Array.from(participantIds);
    let newGuests = [];
    // If the types option was changed, we need to update the guest list
    if (typesChanged) {
      const includeSubscribers = selectedChatType.includes("push");
      const includeMembers = selectedChatType.includes("press");
      const includePerform = selectedChatType.includes("perform");
      const includePull = selectedChatType.includes("pull");
      newGuests = [
        ...new Set(
          activeCommunityChatClientsList
            .filter(
              (u) =>
                (includeSubscribers && u.isSubscriber) ||
                (includeMembers && u.isMember) ||
                (includePerform && u.isPerform) ||
                (includePull && u.isPull)
            )
            .map(({ _id }) => _id)
        ),
      ];
      newGuests = newGuests.filter((id) => !currentParticipants.includes(id));
    }

    const body = {
      option: "edit-subject-type",
      conversationId,
      subject: subjectState,
      types: selectedChatType,
      ownerId: activeUserId,
      currentParticipants,
      organizationMembers: organizationActiveUsers.map((item) => item._id),
      typesChanged,
      newGuests,
    };

    handleModifyConversation(body);
  };

  /**
   * API request to modify a channel and then do the GET request to update the state (if we are blocking/unblocking/make admin/revoke admin)
   * @param {*} body
   */
  const handleModifyConversation = useCallback(
    async (body) => {
      const { option } = body;
      if (option === "edit-subject-type") {
        submit(`/actions/community-chat/modify-conversation`, {
          method: "POST",
          body,
        });
      } else {
        try {
          setIsLoading(true);
          await modifyConversation(api, body);
          const updatedConversation = await getCommunityConversation(
            api,
            conversationId
          );
          setConversationState(updatedConversation);

          flash("success", t("communityChat.conversationUpdated"));
        } catch (e) {
          flash("error", e.message);
        } finally {
          setIsLoading(false);
        }
        submit(`/actions/community-chat/refresh`, {
          method: "POST",
        });
      }
    },
    [api, activeUserId, conversationId, flash]
  );

  return (
    <Modal
      className="edit-channel-modal"
      title={t("communityChat.editChannelModal.title")}
      toClear={["conversationId"]}
    >
      <div className="body">
        <label htmlFor="channel-name">
          {t("communityChat.editChannelModal.channelNameLabel")}
        </label>
        <input
          id="channel-name"
          name="name"
          className="enter-channel-name"
          placeholder={t(
            "communityChat.editChannelModal.channelNamePlaceholder"
          )}
          value={subjectState}
          onChange={({ target: { value } }) => setSubjectState(value)}
        />
        <SelectCommunityChatType
          selectedType={selectedChatType}
          onSelect={setSelectedChatType}
          isEditing={true}
          user={activeUserToRequest}
        />
        <div>
          <span>
            {t("communityChat.editChannelModal.subscribersCount", {
              count: combinedList.length,
            })}
          </span>
          <div className="edit-channel-cards-container">
            {combinedList.map((item) => {
              return (
                <ParticipantCard
                  key={item._id}
                  participant={item}
                  handleParticipantAction={handleParticipantAction}
                  webAppTheme={webAppTheme}
                />
              );
            })}
          </div>
          {isLoading && (
            <div className="spinner-div">
              <img
                src="/img/spinner.gif"
                width="50"
                height="50"
                alt={t("communityChat.spinnerAlt")}
              />
            </div>
          )}
        </div>
      </div>
      <footer>
        <button
          data-button-type="primary"
          disabled={isButtonDisabled()}
          onClick={handleEditChannel}
          type="button"
        >
          {t("communityChat.editChannelModal.save")}
        </button>
      </footer>
    </Modal>
  );
};

EditChannelModal.propTypes = {
  locals: PropTypes.object,
  api: PropTypes.any,
};

/**
 * Function to find the current conversation
 * @param {*} conversationsArray
 * @param {*} conversationId
 * @returns
 */
const findConversationById = (conversationsArray, conversationId) => {
  return (
    conversationsArray.find(({ id }) => id === conversationId) || {
      subject: "",
      custom: {
        blocked: "[]",
        guests: "[]",
      },
      participants: {},
    }
  );
};
